import {
  Button,
  CardContent,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { useContext, useEffect, useState } from "react"
import { MainContext } from "../../controllers/main"
import EmbedSlice from "../../models/bodySlices/embedSlice"
import ImageSlice from "../../models/bodySlices/imageSlice"
import QuoteSlice from "../../models/bodySlices/quoteSlice"
import TextSlice from "../../models/bodySlices/textSlice"
import BlockEditor from "../blockEditor/blockEditor"
import { BodySliceType } from "../../services/config/enum"
import {
  deepCopy,
  isValidURL,
  stripHtml,
  testYtURL,
} from "../../services/utilities/utility"
import constraints from "../../services/config/constraints"
import { CheckInsContext } from "../../controllers/checkIns"
import CheckIn from "../../models/checkIn"

const CheckInTranslationsEdit = ({ itemIndex }: { itemIndex: number }) => {
  const { showPadding } = useContext(MainContext)
  const {
    currentCheckIn,
    setCurrentCheckIn,
    editMode,
    translationsErrors,
    setTranslationsErrors,
    copyDetailsFromDefault,
    copyBodyFromDefault,
  } = useContext(CheckInsContext)

  // current item
  const currentItem = currentCheckIn.document.items[itemIndex]

  // errors managment
  const [titleError, setTitleError] = useState<boolean>(false)
  const [descriptionError, setDescriptionError] = useState<boolean>(false)
  const [bodyError, setBodyError] = useState<boolean>(false)

  // update translations errors on errors change
  useEffect(() => {
    if (titleError || descriptionError || bodyError) {
      translationsErrors.find(
        (item) => item.lang === currentItem.lang
      ).hasErrors = true
    } else {
      translationsErrors.find(
        (item) => item.lang === currentItem.lang
      ).hasErrors = false
    }

    setTranslationsErrors([...translationsErrors])
  }, [titleError, descriptionError, bodyError])

  // update errors on currentItem change
  useEffect(() => {
    if (currentItem.title.length < constraints.checkIn.title.min) {
      setTitleError(true)
    } else {
      setTitleError(false)
    }

    if (currentItem.description.length < constraints.checkIn.description.min) {
      setDescriptionError(true)
    } else {
      setDescriptionError(false)
    }

    let errorInBody = false
    if (currentItem.body) {
      currentItem.body.forEach((bodyItem) => {
        if (
          (bodyItem.sliceType === BodySliceType.BodyTextSlice &&
            !stripHtml(bodyItem.text).length) ||
          (bodyItem.sliceType === BodySliceType.BodyQuoteSlice &&
            !bodyItem.quoteText.length) ||
          (bodyItem.sliceType === BodySliceType.BodyImageSlice &&
            (!bodyItem.imgUrl.length || !isValidURL(bodyItem.imgUrl))) ||
          (bodyItem.sliceType === BodySliceType.BodyEmbedSlice &&
            (!bodyItem.embedUrl.length || !testYtURL(bodyItem.embedUrl)))
        ) {
          errorInBody = true
        }
      })
    }
    if (errorInBody) {
      setBodyError(true)
    } else {
      setBodyError(false)
    }
  }, [currentItem])

  return (
    <CardContent
      style={{
        height:
          showPadding === "yes" ? "calc(100vh - 200px)" : "calc(100vh - 183px)",
        maxHeight:
          showPadding === "yes" ? "calc(100vh - 200px)" : "calc(100vh - 183px)",
        overflowY: "scroll",
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" style={{ height: 28 }}>
          <Typography variant="h6" className="card-title">
            Details
          </Typography>
          <Stack
            alignItems="center"
            justifyContent="flex-end"
            style={{ width: "100%" }}
            direction="row"
            spacing={2}
          >
            <FormGroup style={{ marginRight: -16 }}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={!editMode}
                    checked={currentItem.isDefault}
                    onChange={(e) => {
                      const currentCheckInCopy: CheckIn =
                        deepCopy(currentCheckIn)
                      currentCheckInCopy.document.items[itemIndex].isDefault =
                        e.target.checked
                      if (e.target.checked) {
                        currentCheckInCopy.document.items.forEach(
                          (item, index) => {
                            if (index !== itemIndex) {
                              item.isDefault = false
                            }
                          }
                        )
                      }
                      setCurrentCheckIn(currentCheckInCopy)
                    }}
                  />
                }
                label="Default"
              />
            </FormGroup>
            <Divider orientation="vertical" flexItem />
            <Button
              size="small"
              variant="outlined"
              style={{ height: 26, fontSize: 12 }}
              onClick={() => {
                copyDetailsFromDefault(itemIndex)
              }}
              disabled={
                currentItem.isDefault ||
                !editMode ||
                !currentCheckIn.document.items.filter((item) => item.isDefault)
                  .length
              }
            >
              Copy from default
            </Button>
          </Stack>
        </Stack>
        <Stack gap={2} direction="row">
          <TextField
            fullWidth
            size="small"
            label="Title"
            value={currentItem.title}
            onChange={(e) => {
              const currentCheckInCopy: CheckIn = deepCopy(currentCheckIn)
              currentCheckInCopy.document.items[itemIndex].title =
                e.target.value
              setCurrentCheckIn(currentCheckInCopy)

              // errors check
              if (e.target.value.length < constraints.checkIn.title.min) {
                setTitleError(true)
              } else {
                setTitleError(false)
              }
            }}
            disabled={!editMode}
            error={titleError}
            helperText={
              currentItem.title.length < constraints.checkIn.title.min
                ? "This field is required"
                : ""
            }
            onBlur={() => {
              if (currentCheckIn.document.items[itemIndex].title) {
                const currentCheckInCopy: CheckIn = deepCopy(currentCheckIn)
                currentCheckInCopy.document.items[itemIndex].title =
                  currentCheckInCopy.document.items[itemIndex].title.trim()
                setCurrentCheckIn(currentCheckInCopy)
              }
            }}
          />
          <TextField
            fullWidth
            size="small"
            label="Zone"
            value={currentItem.zone ?? ""}
            onChange={(e) => {
              const currentCheckInCopy: CheckIn = deepCopy(currentCheckIn)
              currentCheckInCopy.document.items[itemIndex].zone = e.target.value
              setCurrentCheckIn(currentCheckInCopy)
            }}
            disabled={!editMode}
            onBlur={() => {
              if (currentCheckIn.document.items[itemIndex].zone) {
                const currentCheckInCopy: CheckIn = deepCopy(currentCheckIn)
                currentCheckInCopy.document.items[itemIndex].zone =
                  currentCheckInCopy.document.items[itemIndex].zone.trim()
                setCurrentCheckIn(currentCheckInCopy)
              }
            }}
          />
        </Stack>
        <TextField
          fullWidth
          size="small"
          label="Description"
          value={currentItem.description}
          onChange={(e) => {
            const currentCheckInCopy: CheckIn = deepCopy(currentCheckIn)
            currentCheckInCopy.document.items[itemIndex].description =
              e.target.value
            setCurrentCheckIn(currentCheckInCopy)

            // errors check
            if (e.target.value.length < constraints.checkIn.description.min) {
              setDescriptionError(true)
            } else {
              setDescriptionError(false)
            }
          }}
          disabled={!editMode}
          error={descriptionError}
          helperText={
            currentItem.description.length < constraints.checkIn.description.min
              ? "This field is required"
              : ""
          }
          onBlur={() => {
            if (currentCheckIn.document.items[itemIndex].description) {
              const currentCheckInCopy: CheckIn = deepCopy(currentCheckIn)
              currentCheckInCopy.document.items[itemIndex].description =
                currentCheckInCopy.document.items[itemIndex].description.trim()
              setCurrentCheckIn(currentCheckInCopy)
            }
          }}
        />
        <Stack spacing={2}>
          <Stack direction="row" style={{ height: 28 }}>
            <Typography variant="h6" className="card-title">
              Body
            </Typography>
            <Stack
              alignItems="center"
              justifyContent="flex-end"
              style={{ width: "100%" }}
              direction="row"
              spacing={2}
            >
              <Button
                size="small"
                variant="outlined"
                style={{ height: 26, fontSize: 12 }}
                onClick={() => {
                  copyBodyFromDefault(itemIndex)
                }}
                disabled={
                  currentItem.isDefault ||
                  !editMode ||
                  !currentCheckIn.document.items.filter(
                    (item) => item.isDefault
                  ).length
                }
              >
                Copy from default
              </Button>
            </Stack>
          </Stack>
          <BlockEditor
            body={currentItem.body ?? []}
            setBody={(
              body: (EmbedSlice | ImageSlice | QuoteSlice | TextSlice)[]
            ) => {
              const currentCheckInCopy: CheckIn = deepCopy(currentCheckIn)
              currentCheckInCopy.document.items[itemIndex].body = body
              setCurrentCheckIn(currentCheckInCopy)
            }}
            editMode={editMode}
          />
        </Stack>
      </Stack>
    </CardContent>
  )
}

export default CheckInTranslationsEdit
